import type { DragEvent } from "react";
import { Plus } from "lucide-react";

import { Button } from "@monorepo-apps/ui/src/components/button";
import { Skeleton } from "@monorepo-apps/ui/src/components/skeleton";

import type { FeatureRow } from "@/infrastructure/supabase/database.types";
import { FeatureCard, FeatureCardSkeleton } from "@/presentation/components/roadmaps/feature-card";
import { DOMAIN_COLOR, DOMAIN_LABEL, DOMAINS, type Domain } from "@/presentation/data/domains";

interface FeatureStackProps {
  features: readonly FeatureRow[];
  isLoading?: boolean;
  onDragStart: (e: DragEvent<HTMLDivElement>, feature: FeatureRow) => void;
  onInfo: (feature: FeatureRow) => void;
  onCreate: () => void;
}

export function FeatureStack({
  features,
  isLoading = false,
  onDragStart,
  onInfo,
  onCreate,
}: FeatureStackProps) {
  const grouped = groupByDomain(features);
  return (
    <aside
      data-slot="feature-stack"
      className="bg-muted/30 flex w-72 shrink-0 flex-col border-r border-[oklch(0.91_0.008_240)]"
    >
      <StackHeader count={features.length} isLoading={isLoading} onCreate={onCreate} />
      <div className="flex-1 overflow-y-auto px-3 py-3">
        {isLoading ? (
          <StackLoading />
        ) : features.length === 0 ? (
          <StackEmpty />
        ) : (
          DOMAINS.map((domain) => {
            const items = grouped.get(domain);
            if (!items || items.length === 0) return null;
            return (
              <StackGroup
                key={domain}
                domain={domain}
                features={items}
                onDragStart={onDragStart}
                onInfo={onInfo}
              />
            );
          })
        )}
      </div>
    </aside>
  );
}

function StackHeader({
  count,
  isLoading,
  onCreate,
}: {
  count: number;
  isLoading: boolean;
  onCreate: () => void;
}) {
  return (
    <div className="flex items-start justify-between gap-2 border-b border-[oklch(0.91_0.008_240)] bg-white/60 px-4 py-3">
      <div>
        <h2 className="text-foreground text-sm font-semibold tracking-tight">Feature stack</h2>
        {isLoading ? (
          <Skeleton className="mt-1 h-3 w-36" />
        ) : (
          <p className="text-muted-foreground mt-0.5 text-[11px]">
            {count} feature{count === 1 ? "" : "s"} without a verdict
          </p>
        )}
      </div>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={onCreate}
        className="h-7 px-2 text-[11px]"
      >
        <Plus className="size-3.5" />
        New
      </Button>
    </div>
  );
}

function StackLoading() {
  return (
    <section data-slot="stack-loading" className="flex flex-col gap-1.5">
      <FeatureCardSkeleton />
      <FeatureCardSkeleton />
      <FeatureCardSkeleton />
      <FeatureCardSkeleton />
    </section>
  );
}

function StackEmpty() {
  return (
    <div className="text-muted-foreground/70 mt-12 text-center text-[11px]">
      Every feature has a verdict on this branch.
    </div>
  );
}

function StackGroup({
  domain,
  features,
  onDragStart,
  onInfo,
}: {
  domain: Domain;
  features: readonly FeatureRow[];
  onDragStart: (e: DragEvent<HTMLDivElement>, feature: FeatureRow) => void;
  onInfo: (feature: FeatureRow) => void;
}) {
  return (
    <section data-slot="stack-group" className="mb-4">
      <header className="mb-1.5 flex items-center gap-1.5 px-1">
        <span
          className="size-2 rounded-full"
          style={{ background: DOMAIN_COLOR[domain] }}
          aria-hidden
        />
        <span className="text-foreground/70 text-[11px] font-medium tracking-tight">
          {DOMAIN_LABEL[domain]}
        </span>
        <span className="text-muted-foreground text-[10px]">({features.length})</span>
      </header>
      <div className="flex flex-col gap-1.5">
        {features.map((f) => (
          <FeatureCard
            key={f.id}
            feature={f}
            variant="stack"
            onDragStart={onDragStart}
            onInfo={onInfo}
          />
        ))}
      </div>
    </section>
  );
}

function groupByDomain(features: readonly FeatureRow[]): Map<Domain, FeatureRow[]> {
  const map = new Map<Domain, FeatureRow[]>();
  for (const f of features) {
    if (!f.domain) continue;
    const bucket = map.get(f.domain) ?? [];
    bucket.push(f);
    map.set(f.domain, bucket);
  }
  return map;
}
